import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getProductById } from '../services/api';
import WishlistButton from '../components/WishlistButton';
import './ProductDetail.css';
import axios from 'axios';

function ProductDetail() {
	const { id, slug } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	
	const [product, setProduct] = useState(null);
	const [quantity, setQuantity] = useState(1);
	const [selectedOption, setSelectedOption] = useState(null);
	const [selectedSize, setSelectedSize] = useState(null);
	const [selectedImage, setSelectedImage] = useState(0);
	const [loading, setLoading] = useState(true);
	const [isZoomed, setIsZoomed] = useState(false);
	const [displayImage, setDisplayImage] = useState(null);
	const [situationTags, setSituationTags] = useState([]);
	const [categories, setCategories] = useState([]);
	const [isAddingToCart, setIsAddingToCart] = useState(false);
	
	// ✅ 이미지 URL 처리 함수
	const getImageUrl = (url) => {
		if (!url) return null;
		if (url.startsWith('/media')) {
			return `https://api.weartoday.kr${url}`;
		}
		if (url.startsWith('http')) {
			return url;
		}
		return `https://api.weartoday.kr/media/${url}`;
	};

	// ✅ 이미지/동영상 분류 (useMemo로 최적화)
	const { videoUrl, imageUrls } = useMemo(() => {
		if (!product?.main_images) {
			return { videoUrl: null, imageUrls: [] };
		}
		
		const allImages = [...product.main_images];
		const video = allImages.find(url => 
			url.toLowerCase().endsWith('.mp4') || 
			url.toLowerCase().endsWith('.webm') ||
			url.toLowerCase().endsWith('.mov')
		);
		const images = allImages.filter(url => 
			!url.toLowerCase().endsWith('.mp4') && 
			!url.toLowerCase().endsWith('.webm') &&
			!url.toLowerCase().endsWith('.mov')
		);
		
		console.log('🎬 동영상:', video);
		console.log('🖼️ 이미지들:', images);
		
		return { videoUrl: video, imageUrls: images };
	}, [product?.main_images]);

	// ✅ 페이지 진입 시 스크롤 상단으로 이동
	useEffect(() => {
		const savedPosition = sessionStorage.getItem('homeScrollPosition');
		
		if (!savedPosition) {
			window.scrollTo(0, 0);
			document.body.scrollTop = 0;
			document.documentElement.scrollTop = 0;
		}
	}, [id]);

	// ✅ 동영상 있으면 자동 선택 (페이지 로드 시)
	useEffect(() => {
		if (product && product.main_images) {
			if (videoUrl) {
				setSelectedImage(-1);
				setDisplayImage(null);
				console.log('🎬 동영상 자동 선택');
			} else if (imageUrls.length > 0) {
				setSelectedImage(0);
				setDisplayImage(imageUrls[0]);
			}
		}
	}, [product, videoUrl, imageUrls]);
	
	useEffect(() => {
		fetchProduct();
		fetchCategories();
	}, [id]);
	
	const fetchCategories = async () => {
		try {
			console.log('📂 카테고리 로딩 시작');
			
			const response = await fetch('https://api.weartoday.kr/api/products/categories/');
			const data = await response.json();
			
			console.log('🔍 전체 응답:', data);
			
			let categoryList = [];
			if (Array.isArray(data)) {
				categoryList = data;
			} else if (data.results && Array.isArray(data.results)) {
				categoryList = data.results;
			}
			
			console.log('🔍 전체 카테고리:', categoryList);
			
			const level1 = categoryList.filter(cat => cat.level === 1);
			
			console.log('✅ Level 0 카테고리:', level1);
			
			setCategories(level1);
		} catch (error) {
			console.error('❌ 카테고리 로딩 실패:', error);
		}
	};
	
	const fetchProduct = async () => {
		try {
			const data = await getProductById(id);
			console.log('🔍 상품 데이터:', data);
			console.log('🔍 parsed_options 순서:', data.parsed_options);
			
			setProduct(data);
			
			if (data.category_path) {
				console.log('📂 카테고리 경로:', data.category_path);
			} else {
				console.log('❌ category_path 없음');
			}
			
			const tagNames = [];
			const SITUATION_TAGS = {
				weather: {
					1: '영하날씨', 2: '쌀쌀한날', 3: '선선한날',
					4: '따뜻한날', 5: '더운날', 6: '비오는날',
					7: '눈오는날', 8: '미세먼지', 9: '바람부는날', 10: '습한날'
				},
				where: {
					11: '출근학교', 12: '데이트', 13: '면접미팅',
					14: '결혼식행사', 15: '여행휴가', 16: '운동헬스',
					17: '집콕홈웨어', 18: '카페쇼핑', 19: '등산아웃도어',
					20: '파티클럽', 21: '공연페스티벌', 22: '캠핑피크닉',
					23: '친구만남', 24: '동네산책', 25: '편의점마트', 26: '일상데일리'
				},
				mood: {
					31: '편하게입고싶어', 32: '예쁘게입고싶어', 33: '섹시하게',
					34: '깔끔하게', 35: '화려하게', 36: '심플하게',
					37: '특별하게', 38: '시크하게', 39: '귀엽게', 40: '멋있게'
				},
				situation: {
					51: '급하게나가야해', 52: '첫만남', 53: '사진찍어야해',
					54: '오래걸어야해', 55: '실내에만있어', 56: '야외활동많아',
					57: '에어컨빵빵', 58: '격식차려야해', 59: '온도차심해', 60: '하루종일밖에'
				},
				season_event: {
					71: '봄나들이', 72: '벚꽃축제', 73: '입학개강',
					74: '여름휴가', 75: '바다수영', 76: '페스티벌',
					77: '가을소풍', 78: '단풍놀이', 79: '할로윈',
					80: '겨울스키', 81: '크리스마스', 82: '연말신년',
					83: '발렌타인데이', 84: '졸업식', 85: '생일파티',
					86: '평일일상', 87: '주말나들이'
				},
				style: {
					91: '캐주얼', 92: '스트릿', 93: '빈티지',
					94: '미니멀', 95: '페미닌', 96: '스포티',
					97: '오피스룩', 98: '로맨틱', 99: '모던', 100: '프레피'
				},
				color_mood: {
					111: '밝은컬러', 112: '어두운컬러', 113: '파스텔',
					114: '비비드', 115: '뉴트럴', 116: '모노톤'
				}
			};
			
			const tagFields = [
				{ field: 'weather_tags', category: 'weather' },
				{ field: 'where_tags', category: 'where' },
				{ field: 'mood_tags', category: 'mood' },
				{ field: 'situation_tags', category: 'situation' },
				{ field: 'season_event_tags', category: 'season_event' },
				{ field: 'style_tags', category: 'style' },
				{ field: 'color_mood_tags', category: 'color_mood' }
			];
			
			tagFields.forEach(({ field, category }) => {
				const codes = data[field];
				console.log(`🏷️ ${field}:`, codes);
				
				if (codes && Array.isArray(codes) && codes.length > 0) {
					codes.forEach(code => {
						if (SITUATION_TAGS[category] && SITUATION_TAGS[category][code]) {
							tagNames.push(`#${SITUATION_TAGS[category][code]}`);
						}
					});
				}
			});
			
			console.log('✅ 최종 태그:', tagNames);
			setSituationTags(tagNames);
			
			if (data.parsed_options && data.parsed_options.length > 0) {
				const firstOption = data.parsed_options[0];
				
				const sizesForFirstOption = data.parsed_options
					.filter(o => o.option_name === firstOption.option_name)
					.map(o => ({
						size: o.size,
						price: o.price,
						base_price: o.base_price,
						stock: o.stock
					}));
				
				const optionWithSizes = {
					...firstOption,
					sizes: sizesForFirstOption
				};
				
				setSelectedOption(optionWithSizes);
				console.log('✅ 첫 옵션 선택:', optionWithSizes);
				
				if (sizesForFirstOption.length > 0) {
					setSelectedSize(sizesForFirstOption[0].size);
					console.log('✅ 첫 사이즈 선택:', sizesForFirstOption[0].size);
				}
			} else {
				const defaultOption = {
					option_name: '기본',
					price: data.price || data.display_price || 0,
					base_price: data.base_price || 0,
					stock: data.stock || 0,
					sizes: []
				};
				setSelectedOption(defaultOption);
				console.log('✅ 기본 옵션 생성:', defaultOption);
			}
			
		} catch (error) {
			console.error('상품 로딩 실패:', error);
			alert('상품을 불러오는데 실패했습니다.');
			navigate('/');
		} finally {
			setLoading(false);
		}
	};

	const handleCategoryClick = (categoryName, level) => {
		const pathArray = product.category_path.split(' > ');
		
		if (level === 0) {
			const category = categories.find(cat => cat.name === categoryName);
			if (category) {
				navigate(`/?cat1=${category.id}`);
			} else {
				navigate('/', {
					state: { selectedCategory: categoryName }
				});
			}
		} else if (level === 1) {
			const cat1 = categories.find(cat => cat.name === pathArray[0]);
			if (cat1 && cat1.children) {
				const cat2 = cat1.children.find(cat => cat.name === categoryName);
				if (cat2) {
					navigate(`/?cat1=${cat1.id}&cat2=${cat2.id}`);
				}
			} else {
				navigate('/', {
					state: {
						selectedCategory: pathArray[0],
						autoSelectLevel2: categoryName
					}
				});
			}
		} else if (level === 2) {
			const cat1 = categories.find(cat => cat.name === pathArray[0]);
			if (cat1 && cat1.children) {
				const cat2 = cat1.children.find(cat => cat.name === pathArray[1]);
				if (cat2 && cat2.children) {
					const cat3 = cat2.children.find(cat => cat.name === categoryName);
					if (cat3) {
						navigate(`/?cat1=${cat1.id}&cat2=${cat2.id}&cat3=${cat3.id}`);
					}
				}
			} else {
				navigate('/', {
					state: {
						selectedCategory: pathArray[0],
						autoSelectLevel2: pathArray[1],
						autoSelectLevel3: categoryName
					}
				});
			}
		}
	};

	const handleTagClick = (tag) => {
		let tagName = tag.replace('#', '').trim();
		tagName = tagName.replace(/\//g, '');
		
		const SITUATION_TAGS = {
			weather: {
				1: '영하날씨', 2: '쌀쌀한날', 3: '선선한날',
				4: '따뜻한날', 5: '더운날', 6: '비오는날',
				7: '눈오는날', 8: '미세먼지', 9: '바람부는날', 10: '습한날'
			},
			where: {
				11: '출근학교', 12: '데이트', 13: '면접미팅',
				14: '결혼식행사', 15: '여행휴가', 16: '운동헬스',
				17: '집콕홈웨어', 18: '카페쇼핑', 19: '등산아웃도어',
				20: '파티클럽', 21: '공연페스티벌', 22: '캠핑피크닉',
				23: '친구만남', 24: '동네산책', 25: '편의점마트', 26: '일상데일리'
			},
			mood: {
				31: '편하게입고싶어', 32: '예쁘게입고싶어', 33: '섹시하게',
				34: '깔끔하게', 35: '화려하게', 36: '심플하게',
				37: '특별하게', 38: '시크하게', 39: '귀엽게', 40: '멋있게'
			},
			situation: {
				51: '급하게나가야해', 52: '첫만남', 53: '사진찍어야해',
				54: '오래걸어야해', 55: '실내에만있어', 56: '야외활동많아',
				57: '에어컨빵빵', 58: '격식차려야해', 59: '온도차심해', 60: '하루종일밖에'
			},
			season_event: {
				71: '봄나들이', 72: '벚꽃축제', 73: '입학개강',
				74: '여름휴가', 75: '바다수영', 76: '페스티벌',
				77: '가을소풍', 78: '단풍놀이', 79: '할로윈',
				80: '겨울스키', 81: '크리스마스', 82: '연말신년',
				83: '발렌타인데이', 84: '졸업식', 85: '생일파티',
				86: '평일일상', 87: '주말나들이'
			},
			style: {
				91: '캐주얼', 92: '스트릿', 93: '빈티지',
				94: '미니멀', 95: '페미닌', 96: '스포티',
				97: '오피스룩', 98: '로맨틱', 99: '모던', 100: '프레피'
			},
			color_mood: {
				111: '밝은컬러', 112: '어두운컬러', 113: '파스텔',
				114: '비비드', 115: '뉴트럴', 116: '모노톤'
			}
		};
		
		let foundCode = null;
		let foundCategory = null;
		
		Object.entries(SITUATION_TAGS).forEach(([category, tags]) => {
			Object.entries(tags).forEach(([code, name]) => {
				if (name === tagName) {
					foundCode = parseInt(code);
					foundCategory = category;
				}
			});
		});
		
		if (foundCode && foundCategory) {
			const params = new URLSearchParams();
			params.set('mode', 'situation');
			params.set(foundCategory, foundCode);
			
			navigate(`/?${params.toString()}`);
		} else {
			navigate('/', {
				state: {
					situationTag: tagName
				}
			});
		}
	};

	const handleOptionChange = (option) => {
		console.log('🔄 옵션 변경:', option);
		console.log('📏 사이즈 목록:', option.sizes);
		
		if (!selectedOption || selectedOption.option_name !== option.option_name) {
			setSelectedOption(option);
			
			if (option.image) {
				setSelectedImage(999);
				setDisplayImage(option.image);
			} else if (imageUrls.length > 0 && !displayImage) {
				setSelectedImage(0);
				setDisplayImage(imageUrls[0]);
			}
		} else {
			setSelectedOption(option);
		}
		
		if (option.sizes && option.sizes.length > 0) {
			const currentSizeExists = option.sizes.some(s => s.size === selectedSize);
			if (!currentSizeExists) {
				setSelectedSize(option.sizes[0].size);
			}
		} else {
			setSelectedSize(null);
		}
	};

	const handleThumbnailClick = (index) => {
		if (index === -1) {
			setSelectedImage(-1);
			setDisplayImage(null);
		} else {
			setSelectedImage(index);
			if (imageUrls[index]) {
				setDisplayImage(imageUrls[index]);
			}
		}
	};

	const handleSizeChange = (size) => {
		setSelectedSize(size);
	};

	const currentPrice = useMemo(() => {
		console.log('💰 가격 계산:', { 
			product: !!product, 
			selectedOption: selectedOption?.option_name, 
			selectedSize 
		});

		if (!product) {
			return 0;
		}
		
		if (!selectedOption) {
			return Number(product.price || product.display_price || 0);
		}
		
		if (selectedSize && selectedOption.sizes && selectedOption.sizes.length > 0) {
			const sizeOption = selectedOption.sizes.find(s => s.size === selectedSize);
			if (sizeOption) {
				const price = Number(sizeOption.final_price || sizeOption.price || selectedOption.price || 0);
				console.log(`✅ 사이즈 가격: ${price.toLocaleString()}원`);
				return price;
			}
		}
		
		const basePrice = Number(selectedOption.price || product.price || 0);
		console.log(`✅ 기본 가격: ${basePrice.toLocaleString()}원`);
		return basePrice;
	}, [product, selectedOption, selectedSize]);

	const currentBasePrice = useMemo(() => {
		if (!product || !selectedOption) return 0;
		
		if (selectedSize && selectedOption.sizes && selectedOption.sizes.length > 0) {
			const sizeOption = selectedOption.sizes.find(s => s.size === selectedSize);
			if (sizeOption && sizeOption.base_price) {
				return Number(sizeOption.base_price);
			}
		}
		
		if (selectedOption.base_price) {
			return Number(selectedOption.base_price);
		}
		
		return Math.floor((currentPrice / 0.35) / 100) * 100;
	}, [product, selectedOption, selectedSize, currentPrice]);

	const handleAddToCart = async () => {
		if (isAddingToCart) return;
		setIsAddingToCart(true);
		
		try {
			const token = localStorage.getItem('access_token');
			
			if (!token) {
				localStorage.setItem('redirectPath', location.pathname);
				alert('로그인이 필요합니다.');
				navigate('/login');
				setIsAddingToCart(false);
				return;
			}

			if (product.parsed_options?.length > 0 && !selectedOption) {
				alert('옵션을 선택해주세요.');
				setIsAddingToCart(false);
				return;
			}
			
			if (selectedOption?.sizes?.length > 0 && !selectedSize) {
				alert('사이즈를 선택해주세요.');
				setIsAddingToCart(false);
				return;
			}

			await axios.post(
				'https://api.weartoday.kr/api/products/cart/',
				{
					product: product.id,
					option_name: selectedOption?.option_name || '',
					size: selectedSize || '',
					quantity: quantity,
					price: currentPrice
				},
				{
					headers: {
						'Authorization': `Bearer ${token}`
					}
				}
			);

			window.dispatchEvent(new Event('cartUpdated'));
			alert('장바구니에 추가되었습니다!');
			
		} catch (error) {
			console.error('장바구니 추가 실패:', error);
			
			if (error.response?.status === 401) {
				alert('로그인이 필요합니다.');
				localStorage.removeItem('access_token');
				localStorage.removeItem('refresh_token');
				localStorage.removeItem('user');
				navigate('/login');
			} else {
				alert('장바구니 추가에 실패했습니다.');
			}
		} finally {
			setIsAddingToCart(false);
		}
	};

	const handleSizeSelect = (sizeOption) => {
		setSelectedSize(sizeOption.size);
	};

	const handleBuyNow = () => {
		if (!selectedOption) {
			alert('옵션을 선택해주세요!');
			return;
		}

		if (selectedOption.sizes && selectedOption.sizes.length > 0 && !selectedSize) {
			alert('사이즈를 선택해주세요!');
			return;
		}

		const token = localStorage.getItem('access_token');
		if (!token) {
			alert('로그인이 필요합니다.');
			navigate('/login');
			return;
		}

		const orderItem = {
			id: product.id,
			name: product.name,
			price: currentPrice,
			quantity: quantity,
			image: product.main_images?.[0] || product.image,
			option: {
				name: selectedOption.option_name,
				size: selectedSize || 'FREE',
				option_name: `${selectedOption.option_name}${selectedSize ? ' / ' + selectedSize : ''}`
			}
		};

		console.log('💳 바로구매 데이터 저장:', orderItem);

		localStorage.setItem('directOrder', JSON.stringify([orderItem]));

		navigate('/checkout');
	};

	const getColorForOption = (optionName) => {
		if (!optionName) return null;
		
		const colorMap = {
			'블랙': '#000000',
			'화이트': '#FFFFFF',
			'그레이': '#808080',
			'브라운': '#8B4513',
			'베이지': '#F5F5DC',
			'네이비': '#000080',
			'핑크': '#FFC0CB',
			'레드': '#FF0000',
			'블루': '#0000FF',
			'그린': '#008000',
			'스톤그린': '#5F9E8F',
			'라즈베리': '#E30B5C',
			'민트': '#98FB98',
			'옐로우': '#FFFF00',
			'퍼플': '#800080',
			'오렌지': '#FFA500',
			'카키': '#BDB76B',
			'와인': '#722F37',
			'아이보리': '#FFFFF0',
			'스카이블루': '#87CEEB'
		};
		
		for (const [color, code] of Object.entries(colorMap)) {
			if (optionName.includes(color)) {
				return code;
			}
		}
		
		return null;
	};

	if (loading) {
		return <div className="loading">로딩 중...</div>;
	}

	if (!product) {
		return <div className="error">상품을 찾을 수 없습니다.</div>;
	}

	const allOptions = product.parsed_options || [];

	return (
		<div className="product-detail-page">
			{product && product.category_path && (
				<div className="detail-category-nav">
					<div className="category-path-wrapper">
						<span className="category-path">
							{product.category_path.split(' > ').map((cat, index, arr) => (
								<React.Fragment key={index}>
									<span 
										className="category-link"
										onClick={() => handleCategoryClick(cat, index)}
										style={{ cursor: 'pointer' }}
									>
										{cat}
									</span>
									{index < arr.length - 1 && ' > '}
								</React.Fragment>
							))}
							{situationTags.length > 0 && (
								<span style={{ 
									marginLeft: '10px',
									fontSize: '14px'
								}}>
									| {situationTags.map((tag, idx) => (
										<span
											key={idx}
											onClick={() => handleTagClick(tag)}
											style={{
												color: '#ffffff',
												cursor: 'pointer',
												marginRight: '5px',
												padding: '2px 8px',
												borderRadius: '12px',
												background: 'rgba(255, 255, 255, 0.1)',
												transition: 'all 0.3s'
											}}
											onMouseEnter={(e) => {
												e.target.style.background = 'rgba(255, 255, 255, 0.2)';
												e.target.style.color = '#ffeb3b';
											}}
											onMouseLeave={(e) => {
												e.target.style.background = 'rgba(255, 255, 255, 0.1)';
												e.target.style.color = '#ffffff';
											}}
										>
											{tag}
										</span>
									))}
								</span>
							)}
						</span>
					</div>
				</div>
			)}
			
			<div className="detail-wrapper">
				<div className="product-detail-container">
					<div className="product-images-section">
						<div className="image-thumbnails">
							{/* ✅ 동영상 썸네일 - 맨 위에 자동재생 미리보기 */}
							{videoUrl && (
								<div 
									className={`thumbnail video-thumbnail ${selectedImage === -1 ? 'active' : ''}`}
									onClick={() => handleThumbnailClick(-1)}
									style={{
										position: 'relative',
										cursor: 'pointer'
									}}
								>
									<video 
										src={getImageUrl(videoUrl)}
										style={{
											width: '100%',
											height: '100%',
											objectFit: 'cover'
										}}
										autoPlay
										muted
										loop
										playsInline
										preload="auto"
									/>
									<div style={{
										position: 'absolute',
										top: '50%',
										left: '50%',
										transform: 'translate(-50%, -50%)',
										width: '24px',
										height: '24px',
										background: 'rgba(0,0,0,0.5)',
										borderRadius: '50%',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										color: 'white',
										fontSize: '10px'
									}}>
										▶
									</div>
								</div>
							)}
							
							{/* ✅ 이미지 썸네일들 */}
							{imageUrls.map((img, index) => (
								<div 
									key={index}
									className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
									onClick={() => handleThumbnailClick(index)}
								>
									<img 
										src={getImageUrl(img)}
										alt={`썸네일 ${index + 1}`}
										onError={(e) => {
											e.target.src = 'https://via.placeholder.com/80?text=No+Image';
										}}
									/>
								</div>
							))}
						</div>

						<div className="main-image-container">
							{selectedImage === -1 && videoUrl ? (
								<video 
									key={videoUrl}
									autoPlay
									muted
									loop
									playsInline
									preload="auto"
									style={{ 
										width: '100%', 
										height: '100%', 
										objectFit: 'contain',
										maxHeight: '600px'
									}}
								>
									<source src={getImageUrl(videoUrl)} type="video/mp4" />
								</video>
							) : displayImage ? (
								<div 
									className={`image-wrapper ${isZoomed ? 'zoomed' : ''}`}
									onClick={() => setIsZoomed(!isZoomed)}
								>
									<img 
										src={getImageUrl(displayImage)}
										alt={product.name}
										onError={(e) => {
											e.target.src = 'https://via.placeholder.com/600?text=No+Image';
										}}
									/>
									{!isZoomed && (
										<div className="zoom-hint">클릭하여 확대</div>
									)}
								</div>
							) : imageUrls.length > 0 ? (
								<div 
									className={`image-wrapper ${isZoomed ? 'zoomed' : ''}`}
									onClick={() => setIsZoomed(!isZoomed)}
								>
									<img 
										src={getImageUrl(imageUrls[0])}
										alt={product.name}
										onError={(e) => {
											e.target.src = 'https://via.placeholder.com/600?text=No+Image';
										}}
									/>
									{!isZoomed && (
										<div className="zoom-hint">클릭하여 확대</div>
									)}
								</div>
							) : (
								<div className="no-image-placeholder">이미지 없음</div>
							)}
						</div>
					</div>

					<div className="product-info-section">
						<h1 className="product-name">{product.name}</h1>

						<div className="price-section">
							{currentBasePrice > currentPrice && (
								<span style={{ 
									fontSize: '14px',
									color: '#999',
									textDecoration: 'line-through',
									marginRight: '8px'
								}}>
									{currentBasePrice.toLocaleString()}원
								</span>
							)}
							
							{currentBasePrice > currentPrice && (
								<span style={{ 
									fontSize: '14px', 
									color: '#ff6b00',
									fontWeight: 'bold',
									marginRight: '8px'
								}}>
									{Math.round((1 - currentPrice / currentBasePrice) * 100)}%
								</span>
							)}
							
							<span className="price-amount">{currentPrice.toLocaleString()}원</span>
						</div>

						{allOptions && allOptions.length > 0 && (
							<div className="option-section" style={{ marginTop: '30px' }}>
								<div className="section-title" style={{
									fontSize: '14px',
									fontWeight: '600',
									marginBottom: '12px',
									color: '#333'
								}}>옵션 선택</div>
								<div style={{
									display: 'grid',
									gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
									gap: '10px'
								}}>
									{[...new Map(allOptions.map(item => [item.option_name, item])).values()]
										.map((option, originalIndex) => {
											const images = product.color_images || [];
											const optionImage = images[originalIndex];
											
											return {
												option,
												originalIndex,
												hasImage: !!optionImage,
												image: optionImage
											};
										})
										.sort((a, b) => {
											if (a.hasImage && !b.hasImage) return -1;
											if (!a.hasImage && b.hasImage) return 1;
											return a.originalIndex - b.originalIndex;
										})
										.map(({ option, originalIndex, image }, displayIndex) => {
											console.log(`옵션 ${displayIndex}: ${option.option_name}, 원본 인덱스: ${originalIndex}, 이미지:`, image);
											
											return (
												<div
													key={displayIndex}
													className={`option-item ${selectedOption?.option_name === option.option_name ? 'active' : ''}`}
													onClick={() => {
														const sizesForOption = allOptions
															.filter(o => o.option_name === option.option_name)
															.map(o => ({
																size: o.size,
																price: o.price,
																base_price: o.base_price,
																stock: o.stock
															}));
														
														const optionWithSizes = {
															...option,
															sizes: sizesForOption
														};
														
														console.log('✅ 옵션 클릭:', optionWithSizes);
														
														handleOptionChange(optionWithSizes);
														
														if (image) {
															setDisplayImage(getImageUrl(image.url));
															setSelectedImage(999);
														}
													}}
													style={{
														display: 'flex',
														flexDirection: 'column',
														alignItems: 'center',
														padding: '10px',
														border: '2px solid',
														borderColor: selectedOption?.option_name === option.option_name ? '#ff6b00' : '#ddd',
														borderRadius: '8px',
														cursor: 'pointer',
														background: selectedOption?.option_name === option.option_name ? '#fff5f0' : 'white',
													}}
												>
													{image ? (
														<img 
															src={getImageUrl(image.url)}
															alt={option.option_name}
															style={{
																width: '80px',
																height: '80px',
																objectFit: 'cover',
																borderRadius: '4px',
																marginBottom: '8px'
															}}
															onError={(e) => {
																console.error(`이미지 로딩 실패: ${option.option_name}`);
																e.target.style.display = 'none';
															}}
														/>
													) : (
														<div style={{
															width: '80px',
															height: '80px',
															background: getColorForOption(option.option_name) || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
															borderRadius: '4px',
															marginBottom: '8px',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															fontSize: '12px',
															color: 'white',
															fontWeight: '600',
															padding: '5px',
															textAlign: 'center',
															wordBreak: 'keep-all'
														}}>
															{option.option_name.slice(0, 6)}
														</div>
													)}
													<span style={{
														fontSize: '13px',
														textAlign: 'center',
														color: selectedOption?.option_name === option.option_name ? '#ff6b00' : '#333',
														marginBottom: '4px',
														wordBreak: 'keep-all',
														lineHeight: '1.3'
													}}>
														{option.option_name}
													</span>
													<span style={{
														fontSize: '12px',
														color: '#ff6b00',
														fontWeight: '600'
													}}>
														{Number(option.price).toLocaleString()}원
													</span>
												</div>
											);
										})}
								</div>
							</div>
						)}

						{selectedOption && selectedOption.sizes && selectedOption.sizes.length > 0 && (
							<div className="option-section" style={{ marginTop: '20px' }}>
								<div className="section-title" style={{
									fontSize: '14px',
									fontWeight: '600',
									marginBottom: '12px',
									color: '#333'
								}}>사이즈 선택</div>
								<div className="size-options" style={{
									display: 'flex',
									gap: '10px',
									flexWrap: 'wrap'
								}}>
									{selectedOption.sizes.map((sizeOption, index) => (
										<button
											key={index}
											className={`size-option ${selectedSize === sizeOption.size ? 'active' : ''}`}
											onClick={() => handleSizeSelect(sizeOption)}
											style={{
												minWidth: '60px',
												padding: '12px 20px',
												border: '2px solid',
												borderColor: selectedSize === sizeOption.size ? '#ff6b00' : '#ddd',
												background: selectedSize === sizeOption.size ? '#ff6b00' : 'white',
												color: selectedSize === sizeOption.size ? 'white' : '#333',
												borderRadius: '8px',
												cursor: 'pointer',
												fontSize: '14px',
												fontWeight: '600',
												transition: 'background 0.2s, color 0.2s, border-color 0.2s'
											}}
										>
											{sizeOption.size}
										</button>
									))}
								</div>
							</div>
						)}

						<div className="option-section" style={{ marginTop: '20px' }}>
							<div className="section-title" style={{
								fontSize: '14px',
								fontWeight: '600',
								marginBottom: '12px',
								color: '#333'
							}}>수량</div>
							<div className="quantity-selector" style={{
								display: 'inline-flex',
								border: '1px solid #ddd',
								borderRadius: '8px',
								overflow: 'hidden'
							}}>
								<button 
									className="qty-btn"
									onClick={() => setQuantity(Math.max(1, quantity - 1))}
									style={{
										width: '40px',
										height: '40px',
										border: 'none',
										background: 'white',
										cursor: 'pointer',
										fontSize: '18px'
									}}
								>
									-
								</button>
								<input 
									type="number" 
									className="qty-input"
									value={quantity}
									onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
									min="1"
									max={product.stock}
									style={{
										width: '60px',
										border: 'none',
										borderLeft: '1px solid #ddd',
										borderRight: '1px solid #ddd',
										textAlign: 'center',
										fontSize: '14px'
									}}
								/>
								<button 
									className="qty-btn"
									onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
									style={{
										width: '40px',
										height: '40px',
										border: 'none',
										background: 'white',
										cursor: 'pointer',
										fontSize: '18px'
									}}
								>
									+
								</button>
							</div>
						</div>

						{(selectedOption || selectedSize) && (
							<div className="selected-summary" style={{
								marginTop: '20px',
								padding: '15px',
								background: '#f8f9fa',
								borderRadius: '8px'
							}}>
								<div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>선택한 옵션</div>
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
									<div>
										{selectedOption && <span style={{ fontSize: '14px', fontWeight: '500' }}>{selectedOption.option_name}</span>}
										{selectedSize && <span style={{ fontSize: '14px', fontWeight: '500' }}> / {selectedSize}</span>}
										<span style={{ fontSize: '14px', color: '#666', marginLeft: '10px' }}>수량: {quantity}개</span>
									</div>
									<span style={{ fontSize: '16px', fontWeight: '700', color: '#ff6b00' }}>
										{(currentPrice * quantity).toLocaleString()}원
									</span>
								</div>
							</div>
						)}

						<div className="total-section" style={{
							marginTop: '20px',
							paddingTop: '20px',
							borderTop: '1px solid #eee',
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center'
						}}>
							<span style={{ fontSize: '16px', fontWeight: '600' }}>총 상품금액</span>
							<span style={{ fontSize: '24px', fontWeight: '700', color: '#ff6b00' }}>
								{(currentPrice * quantity).toLocaleString()}원
							</span>
						</div>

						<div className="button-group" style={{
							display: 'flex',
							gap: '10px',
							marginTop: '30px'
						}}>
							<WishlistButton productId={product.id} size="large" />
							<button 
								className="btn-cart"
								onClick={handleAddToCart}
								style={{
									flex: 1,
									padding: '16px',
									border: '1px solid #ff6b00',
									background: 'white',
									color: '#ff6b00',
									borderRadius: '8px',
									fontSize: '16px',
									fontWeight: '600',
									cursor: 'pointer'
								}}
							>
								장바구니
							</button>
							<button 
								className="btn-buy"
								onClick={handleBuyNow}
								style={{
									flex: 1,
									padding: '16px',
									border: 'none',
									background: '#ff6b00',
									color: 'white',
									borderRadius: '8px',
									fontSize: '16px',
									fontWeight: '600',
									cursor: 'pointer'
								}}
							>
								바로구매
							</button>
						</div>

						<div className="delivery-info" style={{
							marginTop: '30px',
							padding: '20px',
							background: '#f8f9fa',
							borderRadius: '8px'
						}}>
							<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
								<span style={{ fontSize: '14px', color: '#666' }}>배송비</span>
								<span style={{ fontSize: '14px', fontWeight: '500' }}>무료배송</span>
							</div>
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								<span style={{ fontSize: '14px', color: '#666' }}>배송예정</span>
								<span style={{ fontSize: '14px', fontWeight: '500' }}>2-3일 이내</span>
							</div>
						</div>
					</div>
				</div>

				{product.marketing_hooks && product.marketing_hooks.length > 0 && (
					<div className="template-wrapper">
						<div className="template-section template-3">
							<div className="hook-section">
								{product.marketing_hooks.map((hook, index) => (
									<div key={index} className="hook-item">
										<div className="hook-emoji">{hook.emoji}</div>
										<div className="hook-text">{hook.text}</div>
									</div>
								))}
							</div>

							{product.description && (
								<div className="desc-section">
									<div className="desc-title">상품 설명</div>
									<div className="desc-text">
										{product.description}
									</div>
								</div>
							)}
						</div>
					</div>
				)}

				{product.detail_images && product.detail_images.length > 0 && (
					<div className="product-description-section">
						<div className="detail-images">
							{product.detail_images.map((img, index) => (
								<img 
									key={index}
									src={getImageUrl(img)}
									alt={`상세 이미지 ${index + 1}`}
									className="detail-image"
								/>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

export default ProductDetail;